import Store from '@ember-data/store';
import { assert } from '@ember/debug';
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { isNothing } from 'donor-elf-web/lib/object-utils';
import ContactModel, {
  type ContactCustomProperty,
} from 'donor-elf-web/models/contact';
import AbilitiesService from 'donor-elf-web/services/abilities';
import ContactsCache, {
  type ContactCustomPropertyInfo,
  type ContactCustomPropertyVisibility,
} from 'donor-elf-web/services/contacts-cache';
import Settings from 'donor-elf-web/services/settings';
import validateContactCustomProperty from 'donor-elf-web/validations/contact-custom-property';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';

interface ComponentArgs {
  contact: ContactModel;
  property?: ContactCustomProperty;
  propertyInfo?: ContactCustomPropertyInfo;
}

export default class ContactDetailsPropertiesCustomPropertyIndexComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    assert(
      'property or propertyInfo must be present',
      args.property != null || args.propertyInfo != null,
    );
  }

  @service declare abilities: AbilitiesService;
  @service declare contactsCache: ContactsCache;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked editing = false;
  @tracked changeset?: any;
  @tracked showValidationErrors = false;
  @tracked saving = false;
  @tracked removing = false;
  @tracked saveError?: any;

  @tracked showDeletePropertyModal = false;
  @tracked showRenamePropertyModal = false;
  @tracked showReorderPropertiesModal = false;
  @tracked showEditOptionsModal = false;

  @cached
  get property(): ContactCustomProperty {
    if (this.args.property) {
      return this.args.property;
    } else {
      return (
        this.args.contact.customPropertiesByLabel[
          this.args.propertyInfo!.l
        ] || {
          ...this.args.propertyInfo!,
          v: undefined,
        }
      );
    }
  }

  get propertyInfo() {
    return (
      this.args.propertyInfo ||
      this.contactsCache.customPropertiesByLowecaseLabel[
        this.property.l.toLowerCase()
      ]
    );
  }

  get propertyExistsOnContact() {
    return this.args.contact.customPropertiesByLabel[this.property.l] != null;
  }

  get showEditIcon() {
    return (
      this.abilities.hasAbility('edit_contact') && !this.editing && !this.saving
    );
  }

  get customPropertyIsReadonly() {
    return this.contactsCache.customPropertyLabelIsReadonly(this.property.l!);
  }

  get propertyIsHidden() {
    return (
      this.contactsCache.customPropertyUserSettingForDetailPage[this.property.l]
        ?.hide === true
    );
  }

  get valueWasNothing() {
    return isNothing(this.property.v);
  }

  get valueIsCurrentlyNothing() {
    return isNothing(this.changeset?.get('v'));
  }

  @action
  setEditing(val: boolean) {
    this.editing = val;
    if (this.editing) {
      let validation = validateContactCustomProperty({
        contactsCache: this.contactsCache,
        isNewProperty: false,
        allowEmptyVal: true,
      });
      this.changeset = new Changeset(
        this.property,
        lookupValidator(validation),
        validation,
      );
    } else {
      this.changeset = undefined;
    }
  }

  @action
  async save() {
    if (this.valueIsCurrentlyNothing) {
      if (this.valueWasNothing) {
        this.editing = false;
      } else {
        this.remove();
      }
    } else {
      await this.changeset.validate();
      if (this.changeset.get('isValid')) {
        try {
          this.changeset.execute();
          this.editing = false;
          this.saving = true;
          let saveCustomProperty = this.store.createRecord('custom-property', {
            contactId: this.args.contact.id,
            l: this.property.l,
            v: this.property.v,
            t: this.property.t,
            o: this.changeset.get('o'),
          });
          await saveCustomProperty.save();
          await this.args.contact.reload();
          if (saveCustomProperty.customPropertyInfo != null) {
            this.contactsCache.updateCustomPropertyInfo(
              saveCustomProperty.customPropertyInfo,
            );
          }
          this.changeset = undefined;
        } catch (e) {
          this.saveError = e;
          this.editing = true;
        }
        this.saving = false;
      } else {
        this.showValidationErrors = true;
      }
    }
  }

  @action
  async remove() {
    try {
      this.editing = false;
      this.saving = true;
      this.removing = true;
      await this.store
        .createRecord('custom-property', {
          contactId: this.args.contact.id,
          l: this.property.l,
          shouldDestroy: true,
        })
        .save();
      await this.args.contact.reload();
      this.changeset = undefined;
    } catch (e) {
      this.saveError = e;
    }
    this.editing = false;
    this.saving = false;
    this.removing = false;
  }

  @action
  handleCancel() {
    this.editing = false;
    this.saveError = undefined;
  }

  @action
  handleEditVisibility(visibility: ContactCustomPropertyVisibility) {
    this.editing = false;
    this.contactsCache.updateCustomPropertyForDetailPageVisibility(
      this.property.l,
      visibility,
    );
  }

  @action
  handleDeleteProperty() {
    this.showDeletePropertyModal = true;
  }

  @action
  handleCloseDeletePropertyModal() {
    this.showDeletePropertyModal = false;
  }

  @action
  handleRenameProperty() {
    this.showRenamePropertyModal = true;
  }

  @action
  handleCloseRenamePropertyModal() {
    this.showRenamePropertyModal = false;
  }

  @action
  handleReorderProperties() {
    this.showReorderPropertiesModal = true;
  }

  @action
  handleCloseReorderPropertiesModal(saved: boolean) {
    if (saved) {
      this.editing = false;
    }
    this.showReorderPropertiesModal = false;
  }

  @action
  handleEditOptions() {
    this.showEditOptionsModal = true;
  }

  @action
  handleCloseEditOptionsModal() {
    this.showEditOptionsModal = false;
    // toggling this.editing to force a re-render of the component
    this.editing = false;
    next(() => {
      this.editing = true;
    });
  }
}
